import { Component, input } from '@angular/core';

export interface BreadcrumbItem {
  label: string;
  onClick?: () => void;
}

/**
 * Renders a horizontal breadcrumb trail.
 *
 * Pass an array of `BreadcrumbItem` objects where all but the last item
 * should have an `onClick` handler. The last item is treated as the current
 * page and rendered as plain text.
 *
 * @example
 * ```html
 * <dtis-breadcrumb [items]="[
 *   { label: 'Orders', onClick: goToOrders },
 *   { label: 'Order 123', onClick: goToOrder },
 *   { label: 'Ring ABC-001' },
 * ]" />
 * ```
 */
@Component({
  selector: 'dtis-breadcrumb',
  template: `
    <nav aria-label="Breadcrumb" class="flex items-center flex-wrap gap-1 text-label-medium text-onSurfaceVariant mb-4">
      @for (item of items(); track item.label; let last = $last; let first = $first) {
        @if (last) {
          <span class="text-onSurface font-medium px-1" aria-current="page">{{ item.label }}</span>
        } @else {
          <button
            type="button"
            [attr.aria-label]="'Navigate to ' + item.label"
            class="min-h-[44px] px-1 flex items-center cursor-pointer rounded
                   active:opacity-70
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:rounded
                   transition-colors"
            [class.text-primary]="first"
            [class.hover:text-secondary]="first"
            [class.hover:text-primary]="!first"
            [class.hover:underline]="true"
            (click)="item.onClick?.()">
            {{ item.label }}
          </button>
          <svg class="w-3.5 h-3.5 shrink-0 text-outlineVariant" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 18l6-6-6-6"/>
          </svg>
        }
      }
    </nav>
  `,
})
export class BreadcrumbComponent {
  items = input.required<BreadcrumbItem[]>();
}
